import { useCallback, useMemo, useState, useEffect } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Tab, TabList, Box } from '@simplywallst/ui-core';
import type { TabOnClickEvent } from '@simplywallst/ui-core';

import {
  MonitoringPanel,
  MonitoringPanelTabList,
  MonitoringPanelTabPanel,
} from '@/components/MonitoringPanel';

import { useWatchlist } from '@/hooks/useWatchlist';
import type { WatchlistItem } from '@/hooks/useWatchlist';
import { useSortedWatchlistItems } from '../../hooks/useWatchlistSorting';

import { ScrollY } from './styled';
import { Sentry } from '@/components/Sentry';

const Overview = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Overview,
});

const Updates = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Updates,
});

const Notes = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Notes,
});

type WatchlistPanelTab = 'overview' | 'updates' | 'notes';

interface Props {
  watchlistItemId: WatchlistItem['id'] | null;
  setWatchlistItemId: (id: WatchlistItem['id'] | null) => void;
}

const WatchlistPanel = ({ watchlistItemId, setWatchlistItemId }: Props) => {
  const { data: watchlist } = useWatchlist();
  const watchlistItems = useSortedWatchlistItems(watchlist?.items);
  const [selectedTab, selectTab] = useState<WatchlistPanelTab>('overview');
  const { Track, trackEvent } = useTracking({
    modifier: 'sidePanel',
  });

  const selectedWatchlistItem = useMemo(
    () => watchlistItems.find((item) => item.id === watchlistItemId) ?? null,
    [watchlistItemId, watchlistItems]
  );

  const selectedCompany = selectedWatchlistItem?.company ?? null;

  // When the currently selected item is removed (if it doesn't exist, close the panel)
  useEffect(() => {
    if (selectedWatchlistItem === null) setWatchlistItemId(null);
  }, [selectedWatchlistItem, setWatchlistItemId]);

  const handleTabClick = useCallback(
    (event: TabOnClickEvent<WatchlistPanelTab>) => {
      const { value } = event.target;
      selectTab(value);
      trackEvent({ action: 'tabClick', value });
    },
    [trackEvent]
  );

  const handleClose = useCallback(() => {
    setWatchlistItemId(null);
    trackEvent({
      action: 'click',
    });
  }, [setWatchlistItemId, trackEvent]);

  return (
    <Track>
      <MonitoringPanel company={selectedCompany} onClose={handleClose}>
        <ScrollY data-cy-id="watchlist-panel">
          <Box marginX="x2" marginBottom="x1">
            <MonitoringPanelTabList>
              <TabList
                templateRows="min-content"
                templateColumns={`repeat(${3}, max-content)`}
              >
                <Tab<WatchlistPanelTab>
                  value="overview"
                  selected={selectedTab === 'overview'}
                  onClick={handleTabClick}
                  styleType="subnav"
                >
                  Overview
                </Tab>
                <Tab<WatchlistPanelTab>
                  value="updates"
                  selected={selectedTab === 'updates'}
                  onClick={handleTabClick}
                  styleType="subnav"
                >
                  Updates
                </Tab>
                <Tab<WatchlistPanelTab>
                  value="notes"
                  selected={selectedTab === 'notes'}
                  onClick={handleTabClick}
                  styleType="subnav"
                >
                  Notes
                </Tab>
              </TabList>
            </MonitoringPanelTabList>
          </Box>
          <MonitoringPanelTabPanel
            value="overview"
            selected={selectedTab === 'overview'}
          >
            {watchlist && selectedWatchlistItem && selectedCompany ? (
              <Sentry sentryCapture={{ team: 'narwhals' }} globalDisabled>
                <Overview selectTab={selectTab} company={selectedCompany} />
              </Sentry>
            ) : null}
          </MonitoringPanelTabPanel>
          <MonitoringPanelTabPanel
            value="updates"
            selected={selectedTab === 'updates'}
            paddingX="x1spacePx"
          >
            {selectedCompany ? (
              <Updates canonicalURL={selectedCompany.canonicalURL} />
            ) : null}
          </MonitoringPanelTabPanel>
          <MonitoringPanelTabPanel
            value="notes"
            selected={selectedTab === 'notes'}
            paddingX="x2spacePx"
            paddingTop="x1spacePx"
            paddingBottom="x12spacePx"
          >
            <Notes
              uniqueSymbol={selectedCompany?.uniqueSymbol}
              companyId={selectedCompany?.id}
              isFloatingButton
            />
          </MonitoringPanelTabPanel>
        </ScrollY>
      </MonitoringPanel>
    </Track>
  );
};

export default WatchlistPanel;
